import React from 'react';
import { Header } from '@features/header/Header';

import type { StepCell } from '../constants/steps';
import { W2SelectionStepBar } from './W2SelectionStepBar';

interface SelectionScaffoldProps {
  /** Quote total to surface in the header (quote/done phases); omit otherwise. */
  priceOverride?: number | null;
  /**
   * Bottom step-bar cells. Omit (or pass an empty array) to render the scaffold
   * WITHOUT a step bar — the PHQ `/e/:token/done` terminal reuses this chrome but
   * has no `W2SelectionStepBar` step model of its own.
   */
  steps?: StepCell[];
  onStepClick?: (index: number) => void;
  children: React.ReactNode;
}

/**
 * Shared page chrome for the five selection phases: the branded page frame
 * (copied from `W2Onboarding.tsx`), the reused B2C `Header` (logo, greeting,
 * quote total), the phase content, and the optional bottom `W2SelectionStepBar`.
 *
 * The frame is a FIXED-VIEWPORT shell — one viewport tall, header and step bar
 * pinned as non-shrinking top/bottom rails, and `<main>` as the only scroll
 * container. The step bar therefore stays on screen on every step regardless of
 * how tall that step's content is (previously the frame was `min-h-screen`, so
 * long steps grew the page and pushed the bar below the fold). Because every
 * phase renders through this one component, the shell applies flow-wide; no
 * screen sets up its own scrolling.
 * Also reused by the PHQ `/e/:token/done` terminal (`W2DoneScreen`), which omits
 * `steps` — the `/thanks`-style card with no step bar. Retryable terminal states
 * (loading / error / multi-employer / ineligible) keep the standalone `W2Shell`
 * card, whose authenticated variant now mounts the same Header/logout chrome.
 */
export const SelectionScaffold: React.FC<SelectionScaffoldProps> = ({
  priceOverride,
  steps,
  onStepClick,
  children,
}) => {
  return (
    // Fixed viewport shell: the frame is exactly one viewport tall and clips, so
    // the page itself never scrolls. Header and step bar are non-shrinking rails
    // pinned to the top/bottom edges; only <main> between them scrolls.
    // `h-dvh` (not `h-screen`) so mobile browser chrome can't push the bar out of
    // view. Every selection phase renders through here, so all screens share it.
    <div className="mx-auto flex h-dvh max-w-[1920px] flex-col gap-3.5 overflow-hidden bg-background py-3.5 pb-6 lg:gap-6 lg:pt-5">
      <div className="shrink-0">
        <Header priceOverride={priceOverride} showW2Logout />
      </div>
      <main className="min-h-0 flex-1 overflow-y-auto px-6 max-lg:px-4">
        {/*
         * `min-h-full` keeps the two-panel shell stretched to the full scroll
         * viewport when a step's content is short (the panels stay flush with
         * the step bar), while still growing past it when content is tall.
         */}
        <div className="flex min-h-full flex-col">{children}</div>
      </main>
      {steps && steps.length > 0 && (
        <W2SelectionStepBar steps={steps} onStepClick={onStepClick} />
      )}
    </div>
  );
};
